import React from 'react';
import styled from 'styled-components';

import { useMutation } from '@apollo/react-hooks';
import { Form, Formik } from 'formik';
import useReactRouter from 'use-react-router';
import * as Yup from 'yup';
import { LoginStatus, LoginType } from '../interfaces/login';
import { LOG_IN_ACCOUNT } from '../queries/queries';

import Button from '@do/walrus/Button';
import SpinnerButton from '@do/walrus/SpinnerButton';
import Text from '@do/walrus/Text';
import TextInput from '@do/walrus/TextInput';
import Tooltip from '@do/walrus/Tooltip';
import {
  ErrorBanner,
  GithubButton,
  GoogleButton,
  NoUnderlineLink,
  SignInButton
} from './Common';

import colors from '@do/walrus/scss/core/_colors.scss';
import vars from '@do/walrus/scss/core/_variables.scss';
import { deleteCookie } from '@do/walrus/utils/cookie';
import _get from 'lodash/get';
import { PreferredLoginProps } from '../../src/interfaces/login';
import { teamVariant } from '../../src/login-variants';
import { createClient } from '../client';
import {
  FORCE_EXTERNAL,
  LOGIN_ENDPOINT,
  LOGIN_REDIRECT_URL
} from '../constants';
import copy from '../copy';
import {
  addSessionData,
  handleFormError,
  saveSessionData
} from '../utils/gql-utils';
import { replaceInterpolatedVariables } from '../utils/string-utils';

// Set AvatarProps to any because the native src attribute doesn't expect a possible null value
interface AvatarProps {
  src: any;
}

const FormHr = styled.h5`
  display: block;
  text-align: center;
  position: relative;
  margin-top: ${vars.space_3};

  > span {
    color: ${colors.grey_dark};
    background: ${colors.white};
    padding: 0 ${vars.space_3};
    text-transform: uppercase;
  }

  &:before {
    content: '';
    position: absolute;
    width: 100%;
    left: 0;
    top: calc(50% - 1px);
    border: ${vars.border_width_thin} solid ${colors.grey_light};
    z-index: -1;
  }

  + ${SignInButton} {
    margin-top: 0;
  }
`;

const SuccessBanner = styled.div`
  background: ${colors.green_lightest};
  padding: ${vars.whitespace_regular};
  border-radius: ${vars.border_radius};
  border: ${vars.border_width_thin} solid ${colors.green_darkest};
  margin-bottom: ${vars.space_4};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  border-radius: ${vars.border_radius};
  border: ${vars.border_width_thin} solid ${colors.grey};
  margin-bottom: ${vars.space_3};
  padding: ${vars.space_3};
`;

const Avatar = styled.img<AvatarProps>`
  width: ${vars.space_9};
  height: ${vars.space_9};
  border-radius: 50%;
  margin-right: ${vars.space_4};
`;

const Email = styled(Text)`
  word-break: break-word;
`;

const NotYouWrap = styled.div`
  padding: ${vars.space_3} 0 ${vars.space_1};
`;

const ForgotPassword = styled.div`
  margin: ${vars.space_3} 0 ${vars.space_2};
  text-align: center;

  &:not(:last-child) {
    margin-top: ${vars.space_4};
  }
`;

const ForgetPreferredSignInWrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
`;

const FormValidation = Yup.object().shape({
  email: Yup.string()
    .email(copy.form.validation.email_valid)
    .required(copy.form.validation.email_required),
  password: Yup.string().required(copy.form.validation.password_required)
});

const PreferredEmailValidation = Yup.object().shape({
  password: Yup.string().required(copy.form.validation.password_required)
});

interface LoginGqlResponse {
  loginAccount: {
    status: LoginStatus;
    required_next_step: LoginType;
    session_data: string;
    redirect_url: string;
  };
}

interface LoginGqlVariables {
  loginRequest: {
    email: string;
    password: string;
    session_data?: string;
  };
}

interface Props {
  githubOauthUrl: string;
  googleOauthUrl: string;
  passwordSuccessBanner?: 'forgot' | 'reset';
  showNext: (page: LoginType) => void;
  user?: PreferredLoginProps;
  hasPreferredLogin: boolean;
  team?: {
    name: string;
  };
}
const client = createClient(LOGIN_ENDPOINT);

const LoginForm: React.FC<Props> = ({
  githubOauthUrl,
  googleOauthUrl,
  passwordSuccessBanner,
  showNext,
  user,
  hasPreferredLogin,
  team
}) => {
  const [genericServerError, setGenericServerError] = React.useState();
  const [redirecting, setRedirecting] = React.useState(false);
  const [loginAccount, { loading }] = useMutation<
    LoginGqlResponse,
    LoginGqlVariables
  >(LOG_IN_ACCOUNT, { client });

  const { history } = useReactRouter();

  const userEmail = _get(user, 'email');
  const preferredEmail = _get(user, 'sign_in_method') === 'email';
  const preferredGoogle = _get(user, 'sign_in_method') === 'google';
  const preferredGithub = _get(user, 'sign_in_method') === 'github';
  const joinTeamCopy = replaceInterpolatedVariables(team, teamVariant);

  const PREFERRED_SIGN_IN_COOKIE = '_digitalocean_remember_me';
  const TEAM_INVITE_COOKIE = 'invite_code';

  return (
    <Formik
      initialValues={{
        email: team ? userEmail : '',
        password: ''
      }}
      validationSchema={
        preferredEmail ? PreferredEmailValidation : FormValidation
      }
      onSubmit={(values, { setErrors }) => {
        setGenericServerError(null); /* Reset displayed error */

        loginAccount({
          variables: {
            loginRequest: addSessionData(values)
          }
        })
          .then((successResponse) => {
            const {
              status,
              session_data,
              redirect_url,
              required_next_step
            } = _get(successResponse, 'data.loginAccount');
            saveSessionData(session_data);

            if (status === 'granted') {
              setRedirecting(true);
              window.location.assign(redirect_url || LOGIN_REDIRECT_URL);
            } else if (status === 'pending') {
              showNext(required_next_step);
            } else if (status === 'rejected') {
              setGenericServerError(copy.errors.incorrect_email_or_password);
            }
          })
          .catch(handleFormError(setErrors, setGenericServerError));
      }}
      render={({ errors, values, touched }) => (
        <>
          {passwordSuccessBanner && (
            <SuccessBanner>
              {copy.form.login.after_password[passwordSuccessBanner]}
            </SuccessBanner>
          )}

          {team ? (
            <h4>{joinTeamCopy.title}</h4>
          ) : hasPreferredLogin ? (
            <h4>{copy.form.preferred_login_title}</h4>
          ) : (
            <h4>{copy.form.login_title}</h4>
          )}

          {team && <Text>{joinTeamCopy.subtitle}</Text>}

          {hasPreferredLogin && (
            <>
              <Text>{copy.form.preferred_login_details}</Text>
              <UserInfo>
                <Avatar src={_get(user, 'avatar_url')} alt="avatar" />
                <Email>{userEmail}</Email>
                <ForgetPreferredSignInWrapper>
                  <Tooltip content="forget this sign-in" placement="left">
                    <span tabIndex={0}>
                      {/* The above span is necessary for the Tooltip component. See CENG-1691 for more. */}
                      <Button
                        variation="icon"
                        icon="close"
                        onClick={() => {
                          // Delete remember me cookie
                          deleteCookie(PREFERRED_SIGN_IN_COOKIE);

                          // Delete invite_code cookie when choosing to sign in as another user
                          deleteCookie(TEAM_INVITE_COOKIE);

                          // Reload /login with cookie deleted to not display preferred login
                          window.location.reload();
                        }}
                      />
                    </span>
                  </Tooltip>
                </ForgetPreferredSignInWrapper>
              </UserInfo>
            </>
          )}

          {(!hasPreferredLogin || preferredEmail) && (
            <Form autoComplete="none">
              {genericServerError && <ErrorBanner error={genericServerError} />}

              {hasPreferredLogin ? (
                <TextInput
                  placeholder={copy.form.login.password_placeholder}
                  required
                  name="password"
                  label={copy.form.password_label}
                  type="password"
                  className="do-u-mb--medium"
                  touched={touched.password}
                  error={errors.password}
                  value={values.password}
                  autoComplete="none"
                  autoFocus
                />
              ) : (
                <>
                  <TextInput
                    placeholder={copy.form.email_placeholder}
                    required
                    name="email"
                    label={copy.form.email_label}
                    className="do-u-mb--small"
                    touched={touched.email}
                    error={errors.email}
                    value={values.email}
                    autoComplete="none"
                    autoFocus
                  />
                  <TextInput
                    placeholder={copy.form.login.password_placeholder}
                    required
                    name="password"
                    label={copy.form.password_label}
                    type="password"
                    className="do-u-mb--medium"
                    touched={touched.password}
                    error={errors.password}
                    value={values.password}
                    autoComplete="none"
                  />
                </>
              )}

              <SpinnerButton
                type="submit"
                fullWidth={true}
                disabled={loading || redirecting}
                spinning={loading || redirecting}
                loadingMessage={copy.loading.login.belowButton}
              >
                {copy.form.login.button_text}
              </SpinnerButton>
            </Form>
          )}

          {!hasPreferredLogin && (
            <FormHr>
              <span>{copy.form.or}</span>
            </FormHr>
          )}

          {(!hasPreferredLogin || preferredGoogle) && (
            <GoogleButton
              url={googleOauthUrl}
              text={copy.form.oauth.login.google_button}
            />
          )}
          {(!hasPreferredLogin || preferredGithub) && (
            <GithubButton
              url={githubOauthUrl}
              text={copy.form.oauth.login.github_button}
            />
          )}

          {(!hasPreferredLogin || preferredEmail) && (
            <ForgotPassword style={{ textAlign: 'left' }}>
              <NoUnderlineLink
                forceExternal={FORCE_EXTERNAL}
                to="/forgot_password/new"
                onClick={(e: Event) => {
                  history.push({
                    pathname: '/forgot_password/new',
                    state: { email: !hasPreferredLogin && values.email }
                  });
                  e.preventDefault();
                }}
              >
                {copy.form.forgot_password}
              </NoUnderlineLink>
            </ForgotPassword>
          )}

          {hasPreferredLogin && (
            <NotYouWrap>
              <strong>{copy.form.not_you}</strong>
              <br />
              <NoUnderlineLink
                to="/login"
                onClick={(e: Event) => {
                  // Delete invite_code cookie when choosing to sign in as another user
                  deleteCookie(TEAM_INVITE_COOKIE);

                  history.push({
                    pathname: '/login',
                    state: { preferredLoginState: 'none' }
                  });

                  e.preventDefault();

                  // Reload /login with new state to not display preferred login
                  window.location.reload();
                }}
              >
                {copy.form.login_different_account}
              </NoUnderlineLink>
            </NotYouWrap>
          )}
        </>
      )}
    />
  );
};

export default LoginForm;
